import { forwardRef } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

/**
 * Toast — Radix Toast primitives + variant styling.
 *
 * Use the `useToast()` hook to imperatively push toasts; place
 * `<Toaster />` once near the app root for the actual rendering. Direct
 * imports of these primitives are only needed when building a custom
 * toaster (e.g. with a different layout).
 */
export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 right-0 z-100 flex flex-col gap-sm p-lg',
      'w-[420px] max-w-[100vw] outline-hidden',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = 'ToastViewport';

const toastVariants = cva(
  cn(
    'group pointer-events-auto relative flex w-full items-start gap-md',
    'rounded-md border p-md shadow-lg',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[swipe=end]:animate-out data-[swipe=move]:transition-none',
  ),
  {
    variants: {
      variant: {
        info: 'bg-bg-secondary border-border text-text-primary',
        success: 'bg-bg-secondary border-success/40 text-text-primary',
        warning: 'bg-bg-secondary border-warning/40 text-text-primary',
        error: 'bg-bg-secondary border-danger/40 text-text-primary',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

export interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

export const Toast = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  ToastProps
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitive.Root
    ref={ref}
    className={cn(toastVariants({ variant }), className)}
    {...props}
  />
));
Toast.displayName = 'Toast';

export const ToastTitle = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn('text-body-sm font-semibold text-text-primary', className)}
    {...props}
  />
));
ToastTitle.displayName = 'ToastTitle';

export const ToastDescription = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-caption text-text-secondary', className)}
    {...props}
  />
));
ToastDescription.displayName = 'ToastDescription';

export const ToastClose = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => {
  const { t } = useTranslation();
  return (
  <ToastPrimitive.Close
    ref={ref}
    aria-label={t('common.dismiss')}
    className={cn(
      'ml-auto rounded-sm p-xs text-text-muted hover:text-text-primary',
      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent',
      className,
    )}
    {...props}
  >
    <X className="h-3.5 w-3.5" />
  </ToastPrimitive.Close>
  );
});
ToastClose.displayName = 'ToastClose';

const ICONS: Record<NonNullable<ToastProps['variant']>, React.ReactNode> = {
  info: <Info className="h-4 w-4 text-info shrink-0 mt-0.5" />,
  success: <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />,
  warning: <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />,
  error: <AlertCircle className="h-4 w-4 text-danger shrink-0 mt-0.5" />,
};

export function ToastIcon({ variant = 'info' }: { variant?: ToastProps['variant'] }) {
  return <>{ICONS[variant ?? 'info']}</>;
}
